import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Wallet, 
  TrendingDown, 
  Loader, 
  AlertCircle, 
  Clock, 
  CheckCircle,
  XCircle,
  ArrowUpCircle,
  Lock,
  Info
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import DriverLayout from '../../components/driver/DriverLayout';

const DriverWallet = () => {
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      
      // Fetch wallet info (read-only)
      const walletResponse = await api.get('/drivers/wallet');
      setWalletData(walletResponse.data.wallet);
      setTransactions(walletResponse.data.transactions || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load wallet data',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleWithdrawRequest = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(withdrawAmount);
    
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid amount greater than 0',
        variant: 'destructive',
      });
      return;
    }

    if (amount > (walletData?.balance || 0)) {
      toast({
        title: 'Insufficient Balance',
        description: `Your current balance is K${walletData?.balance?.toFixed(2)}`,
        variant: 'destructive',
      });
      return;
    }

    setRequesting(true);

    try {
      const api = (await import('../../utils/api')).default;
      
      await api.post('/drivers/wallet/request-withdrawal', {
        amount: amount,
        paymentMethod: paymentMethod
      });

      toast({
        title: 'Withdrawal Requested',
        description: 'Your withdrawal request has been submitted. Admin will process it shortly.',
      });

      setWithdrawAmount('');
      fetchWalletData();
    } catch (error) {
      console.error('Error requesting withdrawal:', error);
      toast({
        title: 'Request Failed',
        description: error.response?.data?.detail || 'Failed to submit withdrawal request',
        variant: 'destructive',
      });
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <DriverLayout>
        <div className="flex items-center justify-center h-96">
          <Loader className="h-12 w-12 animate-spin text-pink-600" />
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Wallet</h1>
          <p className="text-gray-600 mt-1">View your balance and request withdrawals</p>
        </div>

        {/* Read-Only Notice */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3">
            <Lock className="h-5 w-5 text-blue-600" />
            <div>
              <p className="font-semibold text-blue-900">Read-Only Wallet</p>
              <p className="text-sm text-blue-800">
                Wallet deposits are managed by admin. You can request withdrawals below.
              </p>
            </div>
          </div>
        </Card>

        {/* Warning if balance is low */}
        {walletData?.balance <= 10 && (
          <Card className="p-4 bg-red-50 border-red-200">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <div>
                <p className="font-semibold text-red-900">Low Balance Warning</p>
                <p className="text-sm text-red-800">
                  Your wallet balance is low. Contact admin for a deposit to continue accepting rides.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Balance Card */}
        <Card className="p-8 bg-gradient-to-br from-green-500 to-green-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 mb-2">Current Balance</p>
              <p className="text-5xl font-bold">K{walletData?.balance?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <Wallet className="h-8 w-8" />
            </div>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Earnings</p>
                <p className="text-2xl font-bold text-gray-900">
                  K{walletData?.totalEarnings?.toFixed(2) || '0.00'}
                </p>
              </div>
              <Wallet className="h-8 w-8 text-green-600" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Commission</p>
                <p className="text-2xl font-bold text-red-600">
                  -K{walletData?.totalCommissionPaid?.toFixed(2) || '0.00'}
                </p>
              </div>
              <TrendingDown className="h-8 w-8 text-red-600" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Completed Rides</p>
                <p className="text-2xl font-bold text-gray-900">
                  {walletData?.completedRides || 0}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-purple-600" />
            </div>
          </Card>
        </div>

        {/* Request Withdrawal Form */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <ArrowUpCircle className="h-6 w-6 text-orange-600" />
            Request Withdrawal
          </h2>
          
          <form onSubmit={handleWithdrawRequest} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount (K)</Label>
              <Input
                id="amount"
                type="number"
                placeholder="Enter amount"
                min="1"
                step="0.01"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                disabled={requesting}
                required
              />
              <p className="text-xs text-gray-500">
                Available balance: K{walletData?.balance?.toFixed(2) || '0.00'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentMethod">Payment Method</Label>
              <select
                id="paymentMethod"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                disabled={requesting}
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="mobile_money">Mobile Money</option>
                <option value="cash">Cash Pickup</option>
              </select>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">How it works</p>
                  <p className="text-sm text-yellow-800">
                    Submit a withdrawal request and admin will process it within 24-48 hours.
                    You'll be notified once approved.
                  </p>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800"
              disabled={requesting || !withdrawAmount || parseFloat(withdrawAmount) <= 0}
            >
              {requesting ? 'Submitting...' : 'Request Withdrawal'}
            </Button>
          </form>
        </Card>

        {/* Quick Withdraw Buttons */}
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Quick Amounts</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[50, 100, 200, 500].map((amount) => (
              <Button
                key={amount}
                onClick={() => setWithdrawAmount(amount.toString())}
                variant="outline"
                className="h-16 text-lg font-semibold"
                disabled={amount > (walletData?.balance || 0)}
              >
                K{amount}
              </Button>
            ))}
          </div>
        </Card>

        {/* Transaction History */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Transaction History</h2>
          {transactions.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No transactions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.slice(0, 10).map((tx, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      tx.amount > 0 ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {tx.amount > 0 ? (
                        <ArrowUpCircle className="h-5 w-5 text-green-600 rotate-180" />
                      ) : (
                        <ArrowUpCircle className="h-5 w-5 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 capitalize">
                        {tx.type?.replace('_', ' ') || 'Transaction'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(tx.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.amount > 0 ? '+' : ''}K{tx.amount?.toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Balance: K{tx.balanceAfter?.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Info */}
        <Card className="p-6 bg-gray-50 border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-2">ℹ️ Wallet Information</h3>
          <ul className="text-sm text-gray-700 space-y-2">
            <li>• Maintain a positive balance to continue accepting rides</li>
            <li>• If your balance reaches K0, your account will be automatically suspended</li>
            <li>• Commission (20% of ride price) is deducted upon ride completion</li>
            <li>• Contact admin for wallet deposits or balance inquiries</li>
          </ul>
        </Card>
      </div>
    </DriverLayout>
  );
};

export default DriverWallet;
